import { FunctionComponent } from 'react';
import GenericIcons from '../../../components/generics/GenericIcons';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeMathjax from 'rehype-mathjax';
import '../../../css/PublicWhatWeDoPage.css';

const Component04: FunctionComponent = () => {
  const navigate = useNavigate();
  const goToLoginPage = () => navigate('/public/login');

  return (
    <div className='public-what-we-do-page-component-four-container vdr-wwd-doc'>
      <div className='vdr-section-head vdr-wwd-section-head'>
        <h2 className='public-what-we-do-page-title'>Capacidad de alojamiento de redes</h2>
        <small>Montecarlo</small>
      </div>
      <p className='public-what-we-do-page-text'>
        La herramienta de Capacidad de alojamiento de redes permite realizar estudios detallados
        para estimar la capacidad máxima de generación solar residencial que un alimentador es capaz
        de albergar sin que se superen límites de seguridad o calidad de suministro de la red
        (Capacidad de alojamiento). Además, permite estimar la capacidad de alojamiento de
        alimentadores considerando diferentes medidas de mitigación (como cambios de TAPs, control
        Volt-Var mediante inversores o utilización de baterías residenciales con estrategia de carga
        inteligente). Esta información es sumamente relevante para la toma de decisiones
        estratégica, con el fin de impulsar la integración de recursos energéticos distribuidos y
        cumplir con los objetivos de transición energética mientras se mantienen los estándares de
        calidad y seguridad en la operación de las redes.
      </p>

      <figure style={{ margin: 0 }} className='public-what-we-do-page-component-img-container'>
        <GenericIcons
          icon='montecarloMetodology'
          imageStyle={{
            width: '90%',
            height: 'auto'
          }}
        />
        <figcaption className='public-what-we-do-page-component-img-caption'>
          Figura 5: Metodología de Montecarlo para ejecución de estudios de capacidad de
          alojamiento.
        </figcaption>
      </figure>
      <p className='public-what-we-do-page-text'>
        Las entradas de la herramienta son el modelo en formato OpenDSS del alimentador, los
        perfiles de demanda y los perfiles de irradiación solar, los cuales son asignados a los
        clientes y a los inversores que controlan los sistemas fotovoltaicos respectivamente. La
        estimación de la capacidad de alojamiento de un alimentador se realiza mediante la técnica
        de Montecarlo, que consiste en ejecutar múltiples simulaciones de flujo de potencia para
        diferentes niveles de penetración de recursos energéticos distribuidos. En cada simulación,
        se evalúa el desempeño operativo de la red y se calculan métricas de impacto para niveles de
        voltaje y congestión de líneas y transformadores. Al finalizar las simulaciones, se obtienen
        estadísticos de cada métrica, los cuales se comparan con los límites permitidos por la norma
        para determinar el máximo nivel de integración de recursos energéticos distribuidos. A
        continuación, se indican los criterios que según la normativa técnica deben cumplirse para
        mantener la suficiente seguridad y calidad del suministro:
      </p>

      <ul className='public-what-we-do-page-list vdr-wwd-list--criteria'>
        <li>
          <b>1. Tensión:</b>
          <p>
            En estado normal, el 95% del día los valores eficaces de la tensión en el punto de
            conexión de los clientes, promediados cada 15 minutos, deberán ser tales que la
            regulación de tensión se mantenga dentro de los siguientes límites:
          </p>
          <div className='vdr-wwd-table-wrap'>
            <table className='tabla-tensiones' aria-describedby='tabla2-caption'>
              <caption className='public-what-we-do-page-component-img-caption'>
                Tabla 2: Rangos permitidos de la regulación de voltaje en nodos de conexión de
                clientes.
              </caption>
              <thead>
                <tr>
                  <th scope='col' className='tabla-header'>
                    Tensión de la red \ Densidad de la red
                  </th>
                  <th className='tabla-header'>Alta y Media</th>
                  <th className='tabla-header'>Baja y Muy Baja</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className='tabla-celda'>Baja Tensión</td>
                  <td className='tabla-celda'>± 7,5%</td>
                  <td className='tabla-celda'>± 10,0%</td>
                </tr>
                <tr>
                  <td className='tabla-celda'>Media Tensión</td>
                  <td className='tabla-celda'>± 6,0%</td>
                  <td className='tabla-celda'>± 8,0%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className='public-what-we-do-page-text'>
            La regulación de tensión se calcula mediante la siguiente fórmula:
          </p>

          <div className='public-what-we-do-page-component-four-formula'>
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeMathjax]}>
              {`
$$
\\varDelta V_{k} = \\frac{\\mid V_{k}-V_{n}\\mid}{V_{n}} \\cdot 100
$$
`}
            </ReactMarkdown>
          </div>

          <div className='vdr-wwd-formula-where'>Donde:</div>

          <div className='public-what-we-do-page-component-four-formula-descripcion'>
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeMathjax]}>
              {`
$\\varDelta V_{k}$: Regulación de Tensión en el punto k, en [%].
`}
            </ReactMarkdown>
          </div>

          <div className='public-what-we-do-page-component-four-formula-descripcion'>
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeMathjax]}>
              {`
$V_{k}$: Tensión de suministro en el punto k, determinada como el promedio de las medidas en un intervalo de 15 minutos, en [kV].
`}
            </ReactMarkdown>
          </div>

          <div className='public-what-we-do-page-component-four-formula-descripcion'>
            <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeMathjax]}>
              {`
$V_{n}$: Tensión Nominal en el punto k, en [kV].
`}
            </ReactMarkdown>
          </div>
        </li>
        <li>
          <b>2. Transformadores:</b>
          <ul>
            <li>
              Los valores de corriente, promediados en 15 minutos, no deben superar el límite máximo
              de corriente.
            </li>
            <li>
              Por cada simulación diaria se calcula el número de tramos de líneas y el largo de
              líneas que tiene no cumplen con el criterio anterior
            </li>
          </ul>
        </li>
        <li>
          <b>3. Líneas:</b>
          <ul>
            <li>
              Los valores de corriente, promediados en 15 minutos, no deben superar el límite máximo
              de corriente.
            </li>
            <li>
              Por cada simulación diaria se calcula el número de tramos de líneas y el largo de
              líneas que tiene no cumplen con el criterio anterior.
            </li>
          </ul>
        </li>
      </ul>
      <p className='public-what-we-do-page-text'>
        Finalmente, la herramienta genera tablas y gráficos detallados que permiten visualizar de
        manera clara la capacidad de alojamiento de generación solar residencial en el alimentador,
        así como los niveles de impacto asociados a cada uno de los criterios de seguridad y calidad
        del suministro. Estos resultados facilitan la identificación precisa del factor que limitó
        la capacidad de alojamiento en el alimentador, ya sea en términos de tensión o corriente en
        transformadores o líneas. De este modo, se proporciona una base sólida para la toma de
        decisiones informadas, que permite ajustar la operación de la red o implementar medidas de
        mitigación adecuadas para maximizar la integración de recursos energéticos distribuidos sin
        comprometer la seguridad ni la calidad del suministro.
      </p>
      <p className='public-what-we-do-page-text'>
        Por ejemplo, en la siguiente figura se presentan los resultados de los perfiles de tensión
        de los consumidores residenciales del modelo ante dos situaciones, la primera supone una
        penetración de generación fotovoltaica residencial de un 20% sin implementar ninguna medida
        de mitigación, la segunda supone que además un 40% de los consumidores con sistemas
        fotovoltaicos posee también baterías residenciales con estrategia de carga inteligente. Los
        resultados son claros, el integrar baterías con carga inteligente en conjunto con los
        sistemas fotovoltaicos residenciales prácticamente elimina el problema de sobretensión
        experimentado en el escenario sin baterías.
      </p>
      <figure style={{ margin: 0 }} className='public-what-we-do-page-component-img-container'>
        <GenericIcons
          icon='comparacionPerfilTension'
          imageStyle={{
            width: '50%',
            height: 'auto'
          }}
        />
        <figcaption className='public-what-we-do-page-component-img-caption'>
          Figura 6: Comparación de perfil de tensión, caso con 20% de penetración PV sin BESS
          residencial VS caso con 20% de penetración PV con un 40% de los clientes con PV con BESS
          (Medida de mitigación).
        </figcaption>
      </figure>

      <p className='public-what-we-do-page-text'>
        Otro ejemplo de gráfico de resultados es el siguiente, en el cual se visualiza el porcentaje
        de cargas residenciales con problemas de sobretensión, transformadores con sobrecorriente
        y líneas con sobrecorriente para diferentes niveles de penetración fotovoltaica
        residencial. Del gráfico se desprende que desde un 20% de penetración fotovoltaica ya más de
        un 5% de los clientes experimenta problemas de sobretensión, por tal razón la capacidad de
        alojamiento del alimentador estudiado es de un 10%, es decir que solo el 10% de clientes
        residenciales podría instalar sistemas PV.
      </p>
      <figure style={{ margin: 0 }} className='public-what-we-do-page-component-img-container'>
        <GenericIcons
          icon='porcentajeCargasResidenciales'
          imageStyle={{
            width: '60%',
            height: 'auto'
          }}
        />
        <figcaption className='public-what-we-do-page-component-img-caption'>
          Figura 7: Porcentaje de cargas residenciales con sobretensión, transformadores y líneas
          con sobrecorriente para diferentes niveles de penetración fotovoltaica. Un gráfico del
          que se desprende la capacidad de alojamiento.
        </figcaption>
      </figure>
      <div className='public-what-we-do-page-btn-container'>
        <button className='primary-button-style public-what-we-do-page-btn' onClick={goToLoginPage}>
          Explorar herramienta
          <span aria-hidden='true'>→</span>
        </button>
      </div>
    </div>
  );
};

export default Component04;
